import React from 'react';
import i18n from 'i18next';
import { default as MUIDialog } from 'material-ui/Dialog';
import { useSelector } from 'react-redux';
import { selectPageDialogs } from '@/features/PageLayout/selectors';
import clsx from 'clsx';
import styles from './Dialog.module.scss';

// Explicitly define common Dialog props to fix "Property does not exist" errors
interface IDialogProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  
  // Custom props
  alwaysShowOverlay?: boolean;
  name?: string;
  
  // MUI Props often used
  open: boolean;
  title?: React.ReactNode;
  actions?: React.ReactNode[];
  modal?: boolean;
  autoScrollBodyContent?: boolean;
  contentStyle?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  overlayStyle?: React.CSSProperties;
  actionsContainerClassName?: string;
  onRequestClose?: (buttonClicked: boolean) => void;
  autoDetectWindowHeight?: boolean;
  repositionOnUpdate?: boolean;
  
  [key: string]: any; // Catch-all for other MUI props
}

const inlineStyles = {
  overlayHidden: {
    visibility: 'hidden' as const,
    display: 'none',
  },
  overlayVisible: {
    visibility: 'visible' as const,
    display: 'block',
    transition: 'visibility 10ms',
  },
};

const Dialog: React.FC<IDialogProps> = (props) => {
  // Use modern selector (POJO Array)
  const dialogs = useSelector(selectPageDialogs);

  const getOverlayStyle = () => {
    if (props.alwaysShowOverlay) {
      return props.overlayStyle || inlineStyles.overlayVisible;
    } else {
      if (dialogs && dialogs.length > 0) {
        // Logic: if there is another dialog open (that isn't this one and isn't the first one/root), hide this overlay
        // This suggests a stacking context management for nested dialogs
        const otherDialogExists = dialogs.some((dialog: string, i: number) => dialog !== props.name && i !== 0);
        return otherDialogExists ? inlineStyles.overlayHidden : props.overlayStyle || inlineStyles.overlayVisible;
      }
      return props.overlayStyle || inlineStyles.overlayVisible;
    }
  };

  const { className, style, contentStyle, ...other } = props;

  return (
    <MUIDialog
      repositionOnUpdate={false}
      {...other}
      actionsContainerClassName={styles.actions}
      style={{
        direction: i18n.dir() as 'ltr' | 'rtl',
        display: 'flex',
        paddingTop: 0,
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
      contentStyle={{ transform: '', ...contentStyle }}
      className={clsx('cy-dialog', className)}
      overlayStyle={getOverlayStyle()}
    >
      {props.children}
    </MUIDialog>
  );
};

export default Dialog;